import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, LogOut, Save, X, Eye, EyeOff } from "lucide-react";
import EditFloatingActions from "../components/EditFloatingActions";
import ThemeSelector from "../components/ThemeSelector";
import { fetchOperator, updateOperator } from "../services/profile";
import { logout } from "../services/session";
import {
    getAppLockEnabled,
    getBiometricLockStatus,
    formatBiometricUnavailableMessage,
    isCapacitorRuntime,
    markAppLockSkipPrompt,
    setAppLockEnabled,
    verifyBiometricLock,
} from "../utils/appLock";
import {
    getWeatherCity,
    getWeatherEnabled,
    getWeatherShowHumidity,
    getWeatherShowPressure,
    getWeatherShowWind,
    setWeatherCity,
    setWeatherEnabled,
    setWeatherShowHumidity,
    setWeatherShowPressure,
    setWeatherShowWind,
} from "../utils/weather";

const EditOperator = () => {
    const navigate = useNavigate();
    const [operatorData, setOperatorData] = useState(null);
    const [loggingOut, setLoggingOut] = useState(false);
    const [editedData, setEditedData] = useState({
        operator_name: "",
        phone: "",
        descriptions: "",
        current_password: "",
        new_password: "",
    });
    const [editing, setEditing] = useState(false);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [appLockReady, setAppLockReady] = useState(false);
    const [appLockEnabled, setAppLockEnabledState] = useState(false);
    const [appLockMessage, setAppLockMessage] = useState("");

    const [weatherEnabled, setWeatherEnabledState] = useState(() => getWeatherEnabled());
    const [weatherCityDraft, setWeatherCityDraft] = useState(() => getWeatherCity());
    const [weatherShowWind, setWeatherShowWindState] = useState(() => getWeatherShowWind());
    const [weatherShowPressure, setWeatherShowPressureState] = useState(() => getWeatherShowPressure());
    const [weatherShowHumidity, setWeatherShowHumidityState] = useState(() => getWeatherShowHumidity());

    // 🔁 слухаємо зміну розміру екрана
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!isCapacitorRuntime()) {
            setAppLockReady(true);
            return;
        }

        (async () => {
            try {
                const enabled = await getAppLockEnabled();
                if (cancelled) return;
                setAppLockEnabledState(enabled);
            } catch (_) {
                // ignore
            } finally {
                if (!cancelled) setAppLockReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleToggleAppLock = async (e) => {
        const next = !!e.target.checked;
        setAppLockMessage("");

        if (!isCapacitorRuntime()) return;

        if (next) {
            try {
                const status = await getBiometricLockStatus();
                if (!status.isAvailable) {
                    setAppLockMessage(formatBiometricUnavailableMessage(status.code));
                    return;
                }

                // Skip gate BEFORE system biometric UI (it backgrounds the WebView).
                markAppLockSkipPrompt(15000);
                await verifyBiometricLock({
                    title: "Увімкнути блокування",
                    subtitle: "Підтвердьте особу",
                });
                await setAppLockEnabled(true);
                setAppLockEnabledState(true);
                setAppLockMessage("✅ Блокування увімкнено");
            } catch (err) {
                setAppLockMessage(`❌ ${err?.message || "Не вдалося увімкнути"}`);
            }
            return;
        }

        try {
            await setAppLockEnabled(false);
            setAppLockEnabledState(false);
            setAppLockMessage("✅ Блокування вимкнено");
        } catch (err) {
            setAppLockMessage(`❌ ${err?.message || "Не вдалося вимкнути"}`);
        }
    };

    const handleToggleWeather = (e) => {
        const next = !!e.target.checked;
        setWeatherEnabledState(next);
        setWeatherEnabled(next);
    };

    const handleToggleWeatherShowWind = (e) => {
        const next = !!e.target.checked;
        setWeatherShowWindState(next);
        setWeatherShowWind(next);
    };

    const handleToggleWeatherShowPressure = (e) => {
        const next = !!e.target.checked;
        setWeatherShowPressureState(next);
        setWeatherShowPressure(next);
    };

    const handleToggleWeatherShowHumidity = (e) => {
        const next = !!e.target.checked;
        setWeatherShowHumidityState(next);
        setWeatherShowHumidity(next);
    };

    const commitWeatherCity = () => {
        setWeatherCity(weatherCityDraft);
    };

    // 🔄 завантаження даних оператора
    const loadOperator = useCallback(async () => {
        try {
            const me = await fetchOperator();
            if (!me) throw new Error("Дані оператора не знайдено");
            setOperatorData(me);
            setEditedData({
                operator_name: me.operator_name || "",
                phone: me.phone || "",
                descriptions: me.descriptions || "",
                current_password: "",
                new_password: "",
            });
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOperator();
    }, [loadOperator]);

    const handleEdit = () => {
        setEditing(true);
        setMessage("");
    };

    const handleCancel = () => {
        if (operatorData) {
            setEditedData({
                operator_name: operatorData.operator_name || "",
                phone: operatorData.phone || "",
                descriptions: operatorData.descriptions || "",
                current_password: "",
                new_password: "",
            });
        }
        setEditing(false);
        setMessage("");
        setShowCurrentPassword(false);
        setShowNewPassword(false);
    };

    const handleSave = async () => {
        setMessage("");
        try {
            const payload = {
                operator_name: editedData.operator_name,
                phone: editedData.phone,
                descriptions: editedData.descriptions,
            };
            if (editedData.new_password) {
                payload.current_password = editedData.current_password;
                payload.new_password = editedData.new_password;
            }
            await updateOperator(payload);
            setOperatorData({ ...editedData });
            setEditing(false);
            setEditedData({
                ...editedData,
                current_password: "",
                new_password: "",
            });
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setMessage("✅ Профіль оновлено успішно");
        } catch (e) {
            setMessage(`❌ ${e.message}`);
        }
    };

    const handleLogout = useCallback(async () => {
        if (loggingOut) return;
        const confirmed = window.confirm("Вийти з акаунта оператора?");
        if (!confirmed) return;
        setLoggingOut(true);
        try {
            await logout();
        } catch (_) {
            // still clear local session even if API fails
        }
        localStorage.clear();
        navigate("/");
    }, [loggingOut, navigate]);

    if (loading) return <p>Завантаження…</p>;

    if (!operatorData) {
        return (
            <div>
                <h2>Профіль оператора</h2>
                <p style={{ color: "red" }}>
                    ❌ Не вдалося завантажити профіль. Можливо, сесія завершилась.
                </p>
            </div>
        );
    }

    // --- MOBILE VIEW ---
    if (isMobile) {
        return (
            <div className="config-grid profile-page-grid">
                <h2>Мій профіль</h2>
                {message && <p>{message}</p>}

                <div className="config-card profile-theme-card">
                    <ThemeSelector />
                </div>

                {isCapacitorRuntime() && (
                    <div className="config-card profile-settings-card">
                        <div className="profile-settings-block">
                            <strong>Блокування додатка:</strong>
                            <label className="wr-app-lock-toggle">
                                <input
                                    type="checkbox"
                                    checked={appLockEnabled}
                                    onChange={handleToggleAppLock}
                                    disabled={!appLockReady}
                                />
                                <span>Біометрія / ПІН</span>
                            </label>
                            {appLockMessage && <p style={{ margin: 0 }}>{appLockMessage}</p>}
                        </div>
                    </div>
                )}

                {isCapacitorRuntime() && (
                    <div className="config-card profile-settings-card">
                        <div className="profile-settings-block">
                            <strong>Погода:</strong>
                            <label className="wr-app-lock-toggle" style={{ marginTop: 6 }}>
                                <input type="checkbox" checked={weatherEnabled} onChange={handleToggleWeather} />
                                <span>Показувати на головній стрічці</span>
                            </label>

                            {weatherEnabled && (
                                <div className="profile-weather-block" style={{ marginTop: 8 }}>
                                    <div style={{ marginBottom: 4 }}><strong>Місто:</strong></div>
                                    <input
                                        type="text"
                                        value={weatherCityDraft}
                                        onChange={(e) => setWeatherCityDraft(e.target.value)}
                                        onBlur={commitWeatherCity}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                commitWeatherCity();
                                                e.currentTarget.blur();
                                            }
                                        }}
                                        list="wr-city-list-operator"
                                        placeholder="Напр.: Київ"
                                    />
                                    <datalist id="wr-city-list-operator">
                                        <option value="Київ" />
                                        <option value="Львів" />
                                        <option value="Одеса" />
                                        <option value="Харків" />
                                        <option value="Дніпро" />
                                        <option value="Запоріжжя" />
                                        <option value="Вінниця" />
                                        <option value="Полтава" />
                                        <option value="Чернігів" />
                                        <option value="Черкаси" />
                                        <option value="Івано-Франківськ" />
                                        <option value="Тернопіль" />
                                        <option value="Ужгород" />
                                        <option value="Луцьк" />
                                        <option value="Рівне" />
                                        <option value="Хмельницький" />
                                        <option value="Миколаїв" />
                                        <option value="Херсон" />
                                        <option value="Суми" />
                                        <option value="Житомир" />
                                        <option value="Кропивницький" />
                                    </datalist>

                                    <div style={{ marginTop: 10 }}>
                                        <div style={{ marginBottom: 4 }}><strong>Параметри:</strong></div>
                                        <div className="profile-weather-checks">
                                            <label className="wr-app-lock-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={weatherShowWind}
                                                    onChange={handleToggleWeatherShowWind}
                                                />
                                                <span>Вітер (швидкість/напрям)</span>
                                            </label>
                                            <label className="wr-app-lock-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={weatherShowPressure}
                                                    onChange={handleToggleWeatherShowPressure}
                                                />
                                                <span>Тиск</span>
                                            </label>
                                            <label className="wr-app-lock-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={weatherShowHumidity}
                                                    onChange={handleToggleWeatherShowHumidity}
                                                />
                                                <span>Вологість</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="config-card profile-main-card">
                    <div className={`config-actions operator-card-actions ${editing ? "is-editing is-editing-inline" : "is-idle"}`}>
                        {editing ? null : (
                            <button className="operator-card-action is-primary-edit" onClick={handleEdit} title="Редагувати" aria-label="Редагувати">
                                <Edit size={18} />
                            </button>
                        )}
                    </div>
                    <div className="profile-field-row">
                        <strong>Ім’я оператора:</strong>{" "}
                        {editing ? (
                            <input
                                type="text"
                                value={editedData.operator_name}
                                onChange={(e) =>
                                    setEditedData({ ...editedData, operator_name: e.target.value })
                                }
                            />
                        ) : (
                            <span>{operatorData.operator_name}</span>
                        )}
                    </div>

                    <div className="profile-field-row">
                        <strong>Телефон:</strong>{" "}
                        {editing ? (
                            <input
                                type="text"
                                value={editedData.phone}
                                onChange={(e) =>
                                    setEditedData({ ...editedData, phone: e.target.value })
                                }
                            />
                        ) : (
                            <span>{operatorData.phone}</span>
                        )}
                    </div>

                    <div className="profile-field-row">
                        <strong>Опис:</strong>{" "}
                        {editing ? (
                            <input
                                type="text"
                                value={editedData.descriptions}
                                onChange={(e) =>
                                    setEditedData({ ...editedData, descriptions: e.target.value })
                                }
                            />
                        ) : (
                            <span>{operatorData.descriptions}</span>
                        )}
                    </div>

                    {editing && (
                        <>
                            <div>
                                <strong>Поточний пароль:</strong>
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        style={{ width: "100%", paddingRight: 44 }}
                                        type={showCurrentPassword ? "text" : "password"}
                                        value={editedData.current_password}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                current_password: e.target.value,
                                            })
                                        }
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword((v) => !v)}
                                        title={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                        aria-label={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            color: "#333",
                                            zIndex: 2,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <strong>Новий пароль:</strong>
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        style={{ width: "100%", paddingRight: 44 }}
                                        type={showNewPassword ? "text" : "password"}
                                        value={editedData.new_password}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                new_password: e.target.value,
                                            })
                                        }
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword((v) => !v)}
                                        title={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                        aria-label={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                        style={{
                                            position: "absolute",
                                            right: 10,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "transparent",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            color: "#333",
                                            zIndex: 2,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {editing ? (
                        <EditFloatingActions onSave={handleSave} onCancel={handleCancel} />
                    ) : null}
                </div>

                <div className="config-card profile-logout-card">
                    <strong>Сесія</strong>
                    <p>Вихід із акаунта. Після виходу потрібно знову увійти.</p>
                    <button
                        type="button"
                        className="profile-logout-btn"
                        onClick={handleLogout}
                        disabled={loggingOut}
                    >
                        <LogOut size={16} aria-hidden="true" />
                        {loggingOut ? "Вихід..." : "Вийти з акаунта"}
                    </button>
                </div>

                <div className="config-card profile-delete-card">
                    <div className="profile-delete-card__head">
                        <span aria-hidden="true">⚠️</span>
                        <strong>Видалення акаунту</strong>
                    </div>
                    <p>
                        Бажаєте видалити свій акаунт та всі персональні дані? Зв&apos;яжіться з нашою службою підтримки через форму зворотного зв&apos;язку.
                    </p>
                    <p className="profile-delete-card__note">
                        Розділ <strong>&quot;Служба підтримки&quot;</strong> в меню має форму для надсилання запитів на видалення.
                    </p>
                </div>
            </div>
        );
    }

    // --- DESKTOP VIEW ---
    return (
        <div>
            <h2>Редагування профілю оператора</h2>
            {message && <p>{message}</p>}

            <div className="table-responsive">
                <table>
                    <thead>
                        <tr>
                            <th>Поле</th>
                            <th>Значення</th>
                            <th>Дії</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Ім’я оператора</td>
                            <td>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedData.operator_name}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, operator_name: e.target.value })
                                        }
                                    />
                                ) : (
                                    operatorData.operator_name
                                )}
                            </td>
                            <td rowSpan={7}>
                                {editing ? (
                                    <>
                                        <button onClick={handleSave} title="Зберегти">
                                            <Save size={18} />
                                        </button>
                                        <button
                                            onClick={handleCancel}
                                            title="Відмінити"
                                            style={{ marginLeft: 6 }}
                                        >
                                            <X size={18} />
                                        </button>
                                    </>
                                ) : (
                                    <button onClick={handleEdit} title="Редагувати">
                                        <Edit size={18} />
                                    </button>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Тема</td>
                            <td>
                                <ThemeSelector />
                            </td>
                        </tr>
                        {isCapacitorRuntime() && (
                            <tr>
                                <td>Блокування додатка</td>
                                <td>
                                    <label className="wr-app-lock-toggle">
                                        <input
                                            type="checkbox"
                                            checked={appLockEnabled}
                                            onChange={handleToggleAppLock}
                                            disabled={!appLockReady}
                                        />
                                        <span>Біометрія / ПІН</span>
                                    </label>
                                    {appLockMessage && <div>{appLockMessage}</div>}
                                </td>
                            </tr>
                        )}
                        <tr>
                            <td>Телефон</td>
                            <td>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedData.phone}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, phone: e.target.value })
                                        }
                                    />
                                ) : (
                                    operatorData.phone
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Опис</td>
                            <td>
                                {editing ? (
                                    <input
                                        type="text"
                                        value={editedData.descriptions}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, descriptions: e.target.value })
                                        }
                                    />
                                ) : (
                                    operatorData.descriptions
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Поточний пароль</td>
                            <td>
                                {editing && (
                                    <div style={{ position: "relative", width: "100%" }}>
                                        <input
                                            style={{ width: "100%", paddingRight: 44 }}
                                            type={showCurrentPassword ? "text" : "password"}
                                            value={editedData.current_password}
                                            onChange={(e) =>
                                                setEditedData({
                                                    ...editedData,
                                                    current_password: e.target.value,
                                                })
                                            }
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword((v) => !v)}
                                            title={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                            aria-label={showCurrentPassword ? "Приховати пароль" : "Показати пароль"}
                                            style={{
                                                position: "absolute",
                                                right: 10,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                background: "transparent",
                                                border: "none",
                                                padding: 0,
                                                cursor: "pointer",
                                                color: "#333",
                                                zIndex: 2,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Новий пароль</td>
                            <td>
                                {editing && (
                                    <div style={{ position: "relative", width: "100%" }}>
                                        <input
                                            style={{ width: "100%", paddingRight: 44 }}
                                            type={showNewPassword ? "text" : "password"}
                                            value={editedData.new_password}
                                            onChange={(e) =>
                                                setEditedData({
                                                    ...editedData,
                                                    new_password: e.target.value,
                                                })
                                            }
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword((v) => !v)}
                                            title={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                            aria-label={showNewPassword ? "Приховати пароль" : "Показати пароль"}
                                            style={{
                                                position: "absolute",
                                                right: 10,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                background: "transparent",
                                                border: "none",
                                                padding: 0,
                                                cursor: "pointer",
                                                color: "#333",
                                                zIndex: 2,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="profile-logout-card">
                <strong>Сесія</strong>
                <p>Вихід із акаунта. Після виходу потрібно знову увійти.</p>
                <button
                    type="button"
                    className="profile-logout-btn"
                    onClick={handleLogout}
                    disabled={loggingOut}
                >
                    <LogOut size={16} aria-hidden="true" />
                    {loggingOut ? "Вихід..." : "Вийти з акаунта"}
                </button>
            </div>

            <div className="profile-delete-card">
                <div className="profile-delete-card__head">
                    <span aria-hidden="true">⚠️</span>
                    <h3>Видалення акаунту</h3>
                </div>
                <p>
                    Бажаєте видалити свій акаунт та всі персональні дані? Зв&apos;яжіться з нашою службою підтримки через форму зворотного зв&apos;язку.
                </p>
                <p className="profile-delete-card__note">
                    Розділ <strong>&quot;Служба підтримки&quot;</strong> в меню має форму для надсилання запитів на видалення.
                </p>
            </div>
        </div>
    );
};

export default EditOperator;
